import { Component, Inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UploadService } from '../../../../services/upload.service';
import { FrontendLogService } from '../../../../services/frontend-log.service';
import { DatabaseField, DatabaseFieldsResponse } from '../../../../interfaces/upload.interface';
import { 
  ExcelMappingData, 
  ColumnMapping, 
  CombinedField, 
  MappingResult 
} from '../../../../interfaces/excel-mapping.interface';
import { 
  DataPreviewDialogComponent, 
  PreviewData, 
  ProcessedRecord, 
  MultiValueEntry 
} from '../../../../components/admin';
import { DetailedTableRecord } from '../../../../interfaces/upload.interface';

@Component({
  selector: 'app-excel-mapping-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatCheckboxModule,
    MatProgressSpinnerModule
],
  templateUrl: './excel-mapping-dialog.component.html',
  styleUrl: './excel-mapping-dialog.component.scss'
})
export class ExcelMappingDialogComponent implements OnInit {
  columnMappings: ColumnMapping[] = [];
  combinedFields: CombinedField[] = [];
  validationErrors: string[] = [];
  displayedColumns: string[] = ['excelColumn', 'arrow', 'databaseColumn'];
  isLoadingFields = true;
  enrichExisting = false;  // Meglévő cégek kiegészítése duplikátum esetén
  
  // Cache for validation to prevent ExpressionChangedAfterItHasBeenCheckedError
  private _isValidMappingCache: boolean | null = null;
  private _lastMappingsHash: string = '';

  // Adatbázis mezők az API-tól
  databaseFields: DatabaseField[] = [];

  constructor(
    public dialogRef: MatDialogRef<ExcelMappingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExcelMappingData,
    private uploadService: UploadService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private frontendLogService: FrontendLogService
  ) {}

  ngOnInit(): void {
    // Logok törlése az új feltöltés elején
    this.frontendLogService.clearLogs();
    this.frontendLogService.addLog('🚀 Excel mapping dialog megnyitva');
    this.frontendLogService.addLog(`📁 Fájl: ${this.data.filename}`);
    this.frontendLogService.addLog(`📊 Oszlopok száma: ${this.data.columns.length}`);
    this.frontendLogService.addLog(`📋 Sorok száma: ${this.data.data.length}`);
    
    this.loadDatabaseFields();
    this.initializeCombinedFields();
  }

  private initializeCombinedFields(): void {
    // Automatikusan felismerjük a cím mezőket és létrehozunk egy kombinált mezőt
    const addressKeywords = ['irányítószám', 'település', 'cím', 'város', 'utca', 'házszám', 'address', 'city', 'street'];
    const addressColumns = this.data.columns.filter(column => {
      const columnLower = column.toLowerCase();
      return addressKeywords.some(kw => columnLower.includes(kw));
    });

    // Preferált sorrend: Irányítószám, Település, Cím
    const preferredOrder = ['irányítószám', 'település', 'cím'];
    const sortedColumns = [...addressColumns].sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      const aIdx = preferredOrder.findIndex(kw => aLower.includes(kw));
      const bIdx = preferredOrder.findIndex(kw => bLower.includes(kw));
      const aOrder = aIdx >= 0 ? aIdx : 99;
      const bOrder = bIdx >= 0 ? bIdx : 99;
      return aOrder - bOrder;
    });

    if (sortedColumns.length >= 2) {
      this.combinedFields.push({
        targetDatabaseField: 'companies.address',
        sourceColumns: sortedColumns.slice(0, 3),
        separator: ', ',
        template: sortedColumns.length >= 3 ? '{0} {1}, {2}' : '{0}, {1}'
      });
    }
  }

  /**
   * Kiszűri a nem releváns adatbázis mezőket a lenyíló menüből
   */
  private filterRelevantFields(fields: DatabaseField[]): DatabaseField[] {
    const excludedFields = [
      'id', '_id', 'created_at', 'updated_at', 'létrehozva', 'frissítve',
      'megjegyzések', 'notes', 'comments', 'created', 'modified',
      'timestamp', 'date_created', 'date_modified', 'created_by',
      'updated_by', 'deleted_at', 'is_deleted', 'version',
      'kapcsolat', 'connection', 'contactperson', 'kapcsolattartó'
    ];

    return fields.filter(field => {
      // Kiszűrjük az ID mezőket
      if (field.is_id_field || field.is_auto_generated) {
        return false;
      }

      // Az oszlopnévre pontos egyezést vizsgálunk, különben az 'id' minden *_id mezőre illeszkedne
      const columnName = field.value.split('.').pop()?.toLowerCase() ?? '';
      const isExcluded = excludedFields.some(excluded => columnName === excluded.toLowerCase());

      // Kiszűrjük a field.label alapján is
      // Az 'id' kulcsszó itt kimarad: a címkében ('Opten ID') részszóként is illeszkedne
      const fieldLabelLower = field.label.toLowerCase();
      const isLabelExcluded = excludedFields
        .filter(excluded => excluded !== 'id' && excluded !== '_id')
        .some(excluded => fieldLabelLower.includes(excluded.toLowerCase()));

      return !isExcluded && !isLabelExcluded;
    });
  }

  private loadDatabaseFields(): void {
    this.isLoadingFields = true;
    
    this.uploadService.getDatabaseFields().subscribe({
      next: (response) => {
        this.databaseFields = this.filterRelevantFields(response.fields);
        this.initializeColumnMappings();
        this.isLoadingFields = false;
        
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.autoMapColumns();
          this.cdr.markForCheck();
        }, 0);
      },
      error: (error) => {
        console.error('Hiba az adatbázis mezők betöltése során:', error);
        this.isLoadingFields = false;
        this.cdr.markForCheck();
        // Fallback hardcoded mezők
        this.loadFallbackFields();
      }
    });
  }

  private loadFallbackFields(): void {
    // Fallback adatbázis mezők, ha az API nem elérhető
    const fallbackFields = [
      { value: 'companies.companyname', label: 'Cég név', required: true, table: 'companies', type: 'string' },
      { value: 'companies.address', label: 'Cím', required: false, table: 'companies', type: 'string' },
      { value: 'companies.taxnumber', label: 'Adószám', required: false, table: 'companies', type: 'string' },
      { value: 'companies.companyregistrationnumber', label: 'Cégjegyzékszám', required: false, table: 'companies', type: 'string' },
      { value: 'customerphones.phone', label: 'Telefon', required: false, table: 'customerphones', type: 'string' },
      { value: 'customeremails.email', label: 'Email', required: false, table: 'customeremails', type: 'string' },
      { value: 'companies.website', label: 'Weboldal', required: false, table: 'companies', type: 'string' },
      { value: 'customers.fullname', label: 'Teljes név', required: true, table: 'customers', type: 'string' },
      { value: 'customers.birthdate', label: 'Születési dátum', required: false, table: 'customers', type: 'date' },
      { value: 'customers.birthplace', label: 'Születési hely', required: false, table: 'customers', type: 'string' },
      { value: 'customers.mothername', label: 'Anyja neve', required: false, table: 'customers', type: 'string' },
      { value: 'customeremails.email', label: 'Email cím', required: false, table: 'customeremails', type: 'string' },
      { value: 'customerphones.phone', label: 'Telefonszám', required: false, table: 'customerphones', type: 'string' }
    ];
    
    this.databaseFields = this.filterRelevantFields(fallbackFields);
    this.initializeColumnMappings();
    this.autoMapColumns();
  }

  private initializeColumnMappings(): void {
    this.frontendLogService.addLog('🔍 EXCEL OSZLOPOK: ' + this.data.columns.join(', '));
    this.frontendLogService.addLog('🔍 Tisztségviselők oszlop létezik? ' + this.data.columns.includes('Tisztségviselők'));
    this.columnMappings = this.data.columns.map(column => {
      const sampleData = this.getSampleData(column);
      const columnLower = column.toLowerCase();
      
      // Ellenőrizzük, hogy többértékű mező-e
      const isMultiValue = (columnLower.includes('tisztségviselő') || 
                           columnLower.includes('igazgató') ||
                           columnLower.includes('telefon') ||
                           columnLower.includes('email') ||
                           columnLower.includes('e-mail')) &&
                           !columnLower.includes('száma') && // "Vezetők száma" kizárása
                           !columnLower.includes('számú'); // egyéb "szám" mezők kizárása
      
      let separator: string | undefined = undefined;
      if (isMultiValue) {
        // Többértékű mezőnél ne csak az első sort nézzük, hanem keressük meg az első vesszős példát
        separator = this.detectSeparatorFromColumn(column) || undefined;
      }
      
      return {
        excelColumn: column,
        databaseColumn: '',
        sampleData: sampleData,
        isRequired: false,
        isMultiValue: isMultiValue,
        separator: separator
      };
    });
  }

  private getSampleData(column: string): string {
    const columnIndex = this.data.columns.indexOf(column);
    if (this.data.data.length > 0) {
      const firstRow = this.data.data[0];
      let sampleValue: string = '';
      
      if (Array.isArray(firstRow) && firstRow[columnIndex] !== undefined && firstRow[columnIndex] !== null) {
        sampleValue = String(firstRow[columnIndex]);
      } else if (typeof firstRow === 'object' && firstRow[column] !== undefined && firstRow[column] !== null) {
        sampleValue = String(firstRow[column]);
      }
      
      // Speciális formázás alkalmazása
      sampleValue = this.formatSpecialFields(sampleValue, column);
      
      if (sampleValue && sampleValue.length > 50) {
        return sampleValue.substring(0, 50) + '...';
      }
      
      return sampleValue || 'N/A';
    }
    return 'N/A';
  }

  private formatSpecialFields(value: string, columnName: string): string {
    if (!value) return value;

    const columnLower = columnName.toLowerCase();
    
    // Cégjegyzékszám formázás (01 10 049307)
    if (columnLower.includes('cégjegyzék') || columnLower.includes('cégszám') || columnLower.includes('regszám')) {
      const cleanValue = value.replace(/\s+/g, '').replace(/\D/g, '');
      if (cleanValue.length >= 8) {
        return `${cleanValue.substring(0, 2)} ${cleanValue.substring(2, 4)} ${cleanValue.substring(4)}`;
      }
    }
    
    // Adószám formázás (24061676-1-41)
    if (columnLower.includes('adószám') || columnLower.includes('taxnumber') || columnLower.includes('tax_number')) {
      const cleanValue = value.replace(/[-\s]/g, '').replace(/\D/g, '');
      if (cleanValue.length >= 10) {
        return `${cleanValue.substring(0, 8)}-${cleanValue.substring(8, 9)}-${cleanValue.substring(9)}`;
      }
    }
    
    // Tisztségviselők/többértékű mezők kezelése
    if ((columnLower.includes('tisztségviselő') || 
         columnLower.includes('igazgató') ||
         columnLower.includes('telefon') ||
         columnLower.includes('email') ||
         columnLower.includes('e-mail')) &&
         !columnLower.includes('száma') && 
         !columnLower.includes('számú')) {
      const separator = this.detectSeparator(value);
      if (separator) {
        const items = value.split(separator).map(item => item.trim()).filter(item => item.length > 0);
        if (items.length > 1) {
          return `${items[0]} ${separator} ${items[1]}${items.length > 2 ? ` (${items.length - 2} további)` : ''}`;
        }
      }
    }
    
    return value;
  }

  private detectSeparator(value: string): string | null {
    // Gyakori elválasztó karakterek prioritás szerint
    const separators = [';', ',', '|', '/', '\\n', '\n'];
    
    for (const sep of separators) {
      if (value.includes(sep)) {
        // Ellenőrizzük, hogy valóban több részt választ-e el
        const parts = value.split(sep).map(part => part.trim()).filter(part => part.length > 0);
        if (parts.length > 1) {
          return sep;
        }
      }
    }
    
    return null;
  }

  private detectSeparatorFromColumn(column: string): string | null {
    // Végignézzük az oszlop összes értékét és keressük meg az első vesszős példát
    const columnIndex = this.data.columns.indexOf(column);
    if (columnIndex === -1) return null;
    
    // Ellenőrizzük az első max 50 sort (vagy amennyi van)
    const rowsToCheck = Math.min(this.data.data.length, 50);
    
    for (let i = 0; i < rowsToCheck; i++) {
      const row = this.data.data[i];
      let cellValue: string = '';
      
      if (Array.isArray(row) && row[columnIndex] !== undefined && row[columnIndex] !== null) {
        cellValue = String(row[columnIndex]);
      } else if (typeof row === 'object' && row[column] !== undefined && row[column] !== null) {
        cellValue = String(row[column]);
      }
      
      if (cellValue) {
        const separator = this.detectSeparator(cellValue);
        if (separator) {
          this.frontendLogService.addLog(`✅ Separator találva az oszlopban "${column}": "${separator}" (${i+1}. sorban)`);
          return separator;
        }
      }
    }
    
    this.frontendLogService.addLog(`⚠️ Nem találtam separatort az oszlopban: "${column}"`);
    return null;
  }

  private autoMapColumns(): void {
    this.frontendLogService.addLog('🔍 AUTO-MAPPING KEZDŐDIK');
    this.frontendLogService.addLog('🔍 Database fields: ' + this.databaseFields.map(f => f.value).join(', '));
    
    this.columnMappings.forEach(mapping => {
      const columnName = mapping.excelColumn.toLowerCase();
      this.frontendLogService.addLog(`🔍 Feldolgozás: "${mapping.excelColumn}" (lower: "${columnName}")`);
      
      // Speciális kezelés: Rövidített név → Cégnév
      if (columnName.includes('rövidített név') || columnName === 'rövidített név') {
        const companyNameField = this.databaseFields.find(field => 
          field.value === 'companies.companyname'
        );
        if (companyNameField) {
          mapping.databaseColumn = companyNameField.value;
          mapping.isRequired = companyNameField.required;
          this.frontendLogService.addLog(`✅ MAPPING BEÁLLÍTVA: "${mapping.excelColumn}" → "${mapping.databaseColumn}"`);
          return;
        }
      }
      
      // Speciális Excel oszlop → adatbázis mező párosítások
      const specialMappings: { keywords: string[]; dbField: string }[] = [
        { keywords: ['adószám'], dbField: 'companies.taxnumber' },
        { keywords: ['cégjegyzékszám'], dbField: 'companies.companyregistrationnumber' },
        { keywords: ['opten'], dbField: 'companies.opten_id' },
        { keywords: ['megye'], dbField: 'companies.county' },
        { keywords: ['utolsó létszám', 'létszám'], dbField: 'companies.employeecount' },
        { keywords: ['fő tevékenység', 'főtevékenység'], dbField: 'companies.mainactivity' },
        { keywords: ['nettó árbevétel', 'nettó bevétel'], dbField: 'companies.netrevenue' },
        { keywords: ['telefonszám', 'telefon'], dbField: 'customerphones.phone' },
      ];
      
      const specialMatch = specialMappings.find(sm => 
        sm.keywords.some(kw => columnName.includes(kw))
      );
      
      if (specialMatch) {
        const targetField = this.databaseFields.find(field => field.value === specialMatch.dbField);
        if (targetField) {
          mapping.databaseColumn = targetField.value;
          mapping.isRequired = targetField.required;
          this.frontendLogService.addLog(`✅ MAPPING BEÁLLÍTVA: "${mapping.excelColumn}" → "${mapping.databaseColumn}"`);
          return;
        }
      }
      
      // Speciális kezelés tisztségviselők számára
      if (columnName.includes('tisztségviselő') || columnName.includes('igazgató')) {
        this.frontendLogService.addLog(`✅ Tisztségviselő oszlop felismérve: "${mapping.excelColumn}"`);
        const customerFullnameField = this.databaseFields.find(field => 
          field.value === 'customers.fullname'
        );
        this.frontendLogService.addLog('🔍 customers.fullname mező keresés eredménye: ' + (customerFullnameField ? customerFullnameField.value : 'NINCS'));
        if (customerFullnameField) {
          mapping.databaseColumn = customerFullnameField.value;
          mapping.isRequired = customerFullnameField.required;
          this.frontendLogService.addLog(`✅ MAPPING BEÁLLÍTVA: "${mapping.excelColumn}" → "${mapping.databaseColumn}"`);
          return; // Kilépünk az aktuális iterációból - NE folytassuk az általános auto-mapping-et
        } else {
          this.frontendLogService.addLog('❌ customers.fullname mező NEM TALÁLHATÓ a databaseFields tömbben!');
        }
      }
      
      // Csak akkor próbáljunk általános auto-mapping-et, ha még nincs beállítva databaseColumn
      if (!mapping.databaseColumn) {
        // Automatikus párosítás közös oszlopnevek alapján
        const matchingField = this.databaseFields.find(field => {
          const fieldName = field.value.toLowerCase();
          return (
            fieldName.includes(columnName) ||
            columnName.includes(fieldName.split('_').pop() || '') ||
            this.isColumnNameSimilar(columnName, fieldName)
          );
        });

        if (matchingField) {
          mapping.databaseColumn = matchingField.value;
          mapping.isRequired = matchingField.required;
        }
      }
    });
  }

  private isColumnNameSimilar(excelCol: string, dbField: string): boolean {
    const excelWords = excelCol.split(/[_\s-]+/).map(w => w.toLowerCase());
    const dbWords = dbField.split(/[_\s-]+/).map(w => w.toLowerCase());
    
    return excelWords.some(word => dbWords.includes(word)) ||
           dbWords.some(word => excelWords.includes(word));
  }

  isFieldAlreadyMapped(fieldValue: string, currentIndex: number): boolean {
    if (!fieldValue) return false;
    
    return this.columnMappings.some((mapping, index) => 
      index !== currentIndex && mapping.databaseColumn === fieldValue
    );
  }

  getMappedCount(): number {
    return this.columnMappings.filter(mapping => mapping.databaseColumn).length;
  }

  getRequiredCount(): number {
    return this.databaseFields.filter(field => field.required).length;
  }

  getRequiredMappedCount(): number {
    const requiredFields = this.databaseFields.filter(field => field.required);
    return requiredFields.filter(field => 
      this.columnMappings.some(mapping => mapping.databaseColumn === field.value)
    ).length;
  }

  validateMappings(): void {
    this.validationErrors = [];
    
    // Ellenőrizzük a kötelező mezőket
    const requiredFields = this.databaseFields.filter(field => field.required);
    const mappedRequiredFields = requiredFields.filter(field => 
      this.columnMappings.some(mapping => mapping.databaseColumn === field.value)
    );
    
    if (mappedRequiredFields.length < requiredFields.length) {
      const missingFields = requiredFields.filter(field => 
        !this.columnMappings.some(mapping => mapping.databaseColumn === field.value)
      );
      this.validationErrors.push(`Hiányzó kötelező mezők: ${missingFields.map(f => f.label).join(', ')}`);
    }
    
    // Ellenőrizzük a duplikált mezőket
    const mappedFields = this.columnMappings
      .filter(mapping => mapping.databaseColumn)
      .map(mapping => mapping.databaseColumn);
    
    const duplicates = mappedFields.filter((field, index) => 
      mappedFields.indexOf(field) !== index
    );
    
    if (duplicates.length > 0) {
      this.validationErrors.push(`Duplikált mezők: ${duplicates.join(', ')}`);
    }
    
    // Clear validation cache when validation runs
    this._isValidMappingCache = null;
    this._lastMappingsHash = '';
  }

  isValidMapping(): boolean {
    // Create a hash of current mappings to detect changes
    const currentMappingsHash = JSON.stringify(
      this.columnMappings.map(m => ({ excel: m.excelColumn, db: m.databaseColumn }))
    );
    
    // Return cached result if mappings haven't changed
    if (this._isValidMappingCache !== null && this._lastMappingsHash === currentMappingsHash) {
      return this._isValidMappingCache;
    }
    
    // Only validate if mappings have changed
    if (this._lastMappingsHash !== currentMappingsHash) {
      this.validateMappings();
      this._lastMappingsHash = currentMappingsHash;
    }
    
    // Calculate and cache the result
    this._isValidMappingCache = this.validationErrors.length === 0 && this.getRequiredMappedCount() === this.getRequiredCount();
    
    return this._isValidMappingCache;
  }

  // Method to invalidate validation cache when mappings change
  onMappingChange(): void {
    this._isValidMappingCache = null;
    this._lastMappingsHash = '';
    this.cdr.markForCheck();
  }

  onPreview(): void {
    this.frontendLogService.addLog('🔴 onPreview() HÍVÁS - Mappingek KÖZVETLENÜL a generatePreviewData() előtt');
    this.frontendLogService.addLog('   Mappings: ' + this.columnMappings.map(m => `${m.excelColumn} → ${m.databaseColumn || 'NINCS'}`).join(', '));
    
    if (this.isValidMapping()) {
      // Előnézet csak az első 3 sorral
      const previewData = this.generatePreviewData(3);
      
      // KRITIKUS: Teljes adathalmaz generálása feltöltéshez
      const fullData = this.generatePreviewData(); // Minden sor
      previewData.fullProcessedData = fullData.processedData;
      
      this.frontendLogService.addLog(`📊 Előnézet: ${previewData.processedData.length} sor, Teljes: ${previewData.fullProcessedData.length} sor`);
      
      // Megnyitjuk az előnézeti dialog-ot
      const previewDialogRef = this.dialog.open(DataPreviewDialogComponent, {
        width: '90vw',
        maxWidth: '1200px',
        height: '80vh',
        data: previewData
      });

      // Kezelni a dialog eredményét
      previewDialogRef.afterClosed().subscribe(result => {
        if (result && result.success) {
          this.frontendLogService.addLog('✅ Adatok sikeresen feltöltve');
          // Sikeres feltöltés esetén bezárjuk a mapping dialog-ot is
          this.dialogRef.close({
            success: true,
            result: result.result
          });
        } else if (result && !result.success) {
          this.frontendLogService.addLog('❌ Feltöltési hiba: ' + (result.error || 'Ismeretlen hiba'));
          // Hibaüzenet megjelenítése (később implementálhatjuk)
        }
        // Ha result false vagy üres, akkor a felhasználó csak bezárta a dialog-ot
      });
    } else {
      this.frontendLogService.addLog('⚠️ Hibás párosítás, előnézet nem lehetséges');
    }
  }

  private generatePreviewData(rowLimit?: number): PreviewData {
    this.frontendLogService.addLog('🔍 ÖSSZES MAPPING: ' + this.columnMappings.map(m => `${m.excelColumn} → ${m.databaseColumn || 'NINCS'}`).join(', '));
    
    // Ellenőrizzük a Tisztségviselők mappinget külön
    const tisztsegviseloMapping = this.columnMappings.find(m => m.excelColumn === 'Tisztségviselők');
    this.frontendLogService.addLog('🔍 Tisztségviselők mapping az ÖSSZES listában: ' + (tisztsegviseloMapping ? tisztsegviseloMapping.databaseColumn : 'NINCS'));
    
    const validMappings = this.columnMappings.filter(mapping => mapping.databaseColumn);
    this.frontendLogService.addLog('🔍 VALID MAPPINGS (databaseColumn van): ' + validMappings.map(m => `${m.excelColumn} → ${m.databaseColumn}`).join(', '));
    
    // Ellenőrizzük, hogy a Tisztségviselők benne van-e
    const tisztsegviseloInValid = validMappings.find(m => m.excelColumn === 'Tisztségviselők');
    this.frontendLogService.addLog('🔍 Tisztségviselők a VALID mappings-ben? ' + (tisztsegviseloInValid ? 'IGEN' : 'NEM'));
    
    const processedData: ProcessedRecord[] = [];

    // Sorok korlátozása, ha van limit megadva (előnézet esetén)
    const allRows = rowLimit ? this.data.data.slice(0, rowLimit) : this.data.data;
    
    if (rowLimit) {
      this.frontendLogService.addLog(`📊 Előnézet mód: Csak az első ${rowLimit} sor feldolgozása (összes: ${this.data.data.length})`);
    }

    allRows.forEach((row, rowIndex) => {
      const record: ProcessedRecord = {
        rowIndex: rowIndex + 1, // 1-based indexing
        tables: {},
        multiValueEntries: [],
        detailedRecords: []
      };

      // Kezdő ID-k (előnézet célból)
      let nextCompanyId = rowIndex + 1;
      let nextCustomerId = rowIndex + 8;
      let nextEmailId = rowIndex * 3 + 10;
      let nextPhoneId = rowIndex * 2 + 11;

      validMappings.forEach(mapping => {
        const columnIndex = this.data.columns.indexOf(mapping.excelColumn);
        let cellValue: string = '';

        // Excel adat kinyerése
        if (Array.isArray(row) && row[columnIndex] !== undefined && row[columnIndex] !== null) {
          cellValue = String(row[columnIndex]);
        } else if (typeof row === 'object' && row[mapping.excelColumn] !== undefined && row[mapping.excelColumn] !== null) {
          cellValue = String(row[mapping.excelColumn]);
        }

        if (cellValue) {
          // EREDETI cellValue mentése a split művelethez
          const originalCellValue = cellValue;

          // Tábla és mező név kinyerése
          const [tableName, fieldName] = mapping.databaseColumn.split('.', 2);
          const fullFieldName = mapping.databaseColumn;

          // Tábla inicializálása ha szükséges
          if (!record.tables[tableName]) {
            record.tables[tableName] = {
              tableName: tableName,
              fields: {}
            };
          }

          // Többértékű mezők kezelése
          if (mapping.isMultiValue) {
            // Ha nincs separator beállítva, próbáljuk meg detektálni az aktuális cellából
            let separator = mapping.separator;
            if (!separator) {
              separator = this.detectSeparator(originalCellValue) || ','; // Alapértelmezett: vessző
            }
            
            if (separator) {
              // EREDETI adatból dolgozunk a split művelethez!
              const values = originalCellValue.split(separator)
                .map(val => val.trim())
                .filter(val => val.length > 0);
              
              if (values.length > 1) {
                // Több érték esetén: részletes rekordok generálása
                this.generateDetailedRecords(
                  record, 
                  tableName, 
                  fullFieldName, 
                  values, 
                  nextCompanyId,
                  nextCustomerId,
                  nextEmailId,
                  nextPhoneId
                );
                
                // Többértékű mező: több rekord fog keletkezni
                record.multiValueEntries.push({
                  tableName: tableName,
                  fieldName: fullFieldName,
                  values: values,
                  separator: separator,
                  recordCount: values.length
                });
                
                // KRITIKUS: customerphones táblánál NE tároljuk a vesszős stringet!
                // A backend _process_multi_value_fields fogja külön rekordokként kezelni
                if (tableName !== 'customerphones') {
                  // ÖSSZES ÉRTÉK tárolása a fő táblában (vesszővel elválasztva a log bejegyzéshez)
                  record.tables[tableName].fields[fullFieldName] = values.join(', ');
                }
              } else if (values.length === 1) {
                // Egy érték esetén is generálunk részletes rekordot
                this.generateDetailedRecords(
                  record, 
                  tableName, 
                  fullFieldName, 
                  values, 
                  nextCompanyId,
                  nextCustomerId,
                  nextEmailId,
                  nextPhoneId
                );
                
                // Egyértékű mező esetén a fő táblában is megjelenítjük
                record.tables[tableName].fields[fullFieldName] = values[0];
              } else {
                // Üres érték esetén - speciális formázás alkalmazása
                const formattedValue = this.formatSpecialFields(originalCellValue, mapping.excelColumn);
                record.tables[tableName].fields[fullFieldName] = formattedValue;
              }
            } else {
              // Separator nélküli többértékű mező - speciális formázás alkalmazása
              const formattedValue = this.formatSpecialFields(originalCellValue, mapping.excelColumn);
              record.tables[tableName].fields[fullFieldName] = formattedValue;
              this.addSingleValueToDetailedRecords(record, tableName, fullFieldName, originalCellValue, nextCompanyId, nextCustomerId, nextEmailId, nextPhoneId);
            }
          } else {
            // Nem többértékű mező - speciális formázás alkalmazása
            const formattedValue = this.formatSpecialFields(originalCellValue, mapping.excelColumn);
            record.tables[tableName].fields[fullFieldName] = formattedValue;
            
            // Egyértékű mezők esetén is generálunk részletes rekordot
            this.addSingleValueToDetailedRecords(record, tableName, fullFieldName, originalCellValue, nextCompanyId, nextCustomerId, nextEmailId, nextPhoneId);
          }
        }
      });

      // Kombinált mezők feldolgozása
      this.combinedFields.forEach(combined => {
        if (combined.targetDatabaseField && combined.sourceColumns.length > 0) {
          const combinedValue = this.generateCombinedValue(combined, row);
          if (combinedValue) {
            const [tableName, fieldName] = combined.targetDatabaseField.split('.', 2);
            const fullFieldName = combined.targetDatabaseField;

            // Tábla inicializálása ha szükséges
            if (!record.tables[tableName]) {
              record.tables[tableName] = {
                tableName: tableName,
                fields: {}
              };
            }

            // Kombinált érték hozzáadása
            record.tables[tableName].fields[fullFieldName] = combinedValue;
            
            // Egyértékű mezőként kezelés a részletes rekordokban
            this.addSingleValueToDetailedRecords(record, tableName, fullFieldName, combinedValue, nextCompanyId, nextCustomerId, nextEmailId, nextPhoneId);
          }
        }
      });

      processedData.push(record);
    });

    return {
      mappings: validMappings,
      excelData: allRows,
      processedData: processedData,
      enrichExisting: this.enrichExisting
    };
  }

  private generateDetailedRecords(
    record: ProcessedRecord, 
    tableName: string, 
    fieldName: string, 
    values: string[],
    companyId: number,
    customerId: number,
    emailId: number,
    phoneId: number
  ): void {
    
    values.forEach((value, index) => {
      let detailedRecord = record.detailedRecords!.find((dr: DetailedTableRecord) => dr.tableName === tableName);
      
      if (!detailedRecord) {
        detailedRecord = {
          tableName: tableName,
          tableLabel: this.getTableDisplayName(tableName),
          records: []
        };
        record.detailedRecords!.push(detailedRecord);
      }

      const recordData: any = {};
      
      if (tableName === 'customeremails') {
        recordData[`${tableName}.emailid`] = emailId + index;
        recordData[`${tableName}.customerid`] = null;
        recordData[`${tableName}.companyid`] = `${companyId} (hivatkozás a cégre)`;
        recordData[fieldName] = value;
      } else if (tableName === 'customerphones') {
        recordData[`${tableName}.phoneid`] = phoneId + index;
        recordData[`${tableName}.customerid`] = null;
        recordData[`${tableName}.companyid`] = `${companyId} (hivatkozás a cégre)`;
        recordData[fieldName] = value;
      } else if (tableName === 'customers') {
        // Tisztségviselők esetén minden név külön customer rekord lesz
        recordData[`${tableName}.customerid`] = customerId + index;
        recordData[`${tableName}.companyid`] = `${companyId} (hivatkozás a cégre)`;
        recordData[fieldName] = value; // Egyenként a nevek
      }

      detailedRecord.records.push({
        id: tableName === 'customeremails' ? emailId + index : 
            tableName === 'customerphones' ? phoneId + index : 
            customerId + index,
        fields: recordData
      });
    });
  }

  private addSingleValueToDetailedRecords(
    record: ProcessedRecord,
    tableName: string,
    fieldName: string,
    value: string,
    companyId: number,
    customerId: number,
    emailId: number,
    phoneId: number
  ): void {
    let detailedRecord = record.detailedRecords!.find((dr: DetailedTableRecord) => dr.tableName === tableName);
    
    if (!detailedRecord) {
      detailedRecord = {
        tableName: tableName,
        tableLabel: this.getTableDisplayName(tableName),
        records: [{
          id: tableName === 'companies' ? companyId : 
              tableName === 'customers' ? customerId :
              tableName === 'customeremails' ? emailId :
              phoneId,
          fields: {}
        }]
      };
      record.detailedRecords!.push(detailedRecord);
    }

    // Hozzáadjuk az ID mezőket és kapcsolatokat
    const targetRecord = detailedRecord.records[0];
    if (tableName === 'companies') {
      targetRecord.fields['companies.id'] = companyId;
    } else if (tableName === 'customers') {
      targetRecord.fields['customers.customerid'] = customerId;
      targetRecord.fields['customers.companyid'] = `${companyId} (hivatkozás a cégre)`;
    } else if (tableName === 'customeremails') {
      targetRecord.fields['customeremails.emailid'] = emailId;
      targetRecord.fields['customeremails.customerid'] = null;
      targetRecord.fields['customeremails.companyid'] = `${companyId} (hivatkozás a cégre)`;
    } else if (tableName === 'customerphones') {
      targetRecord.fields['customerphones.phoneid'] = phoneId;
      targetRecord.fields['customerphones.customerid'] = null;
      targetRecord.fields['customerphones.companyid'] = `${companyId} (hivatkozás a cégre)`;
    }

    // Hozzáadjuk a tényleges adatot
    targetRecord.fields[fieldName] = value;
  }

  private getTableDisplayName(tableName: string): string {
    const displayNames: { [key: string]: string } = {
      'companies': 'Cég adatok',
      'customers': 'Tisztségviselők',
      'customeremails': 'Email-címek',
      'customerphones': 'Telefonszámok'
    };
    return displayNames[tableName] || tableName;
  }

  onConfirm(): void {
    if (this.isValidMapping()) {
      // Adatok előkészítése és data-preview-dialog megnyitása
      const previewData = this.generatePreviewData();
      
      // Megnyitjuk az előnézeti dialog-ot
      const previewDialogRef = this.dialog.open(DataPreviewDialogComponent, {
        width: '90vw',
        maxWidth: '1200px',
        height: '80vh',
        data: previewData
      });

      // Kezelni a dialog eredményét
      previewDialogRef.afterClosed().subscribe(result => {
        if (result && result.success) {
          this.frontendLogService.addLog('✅ Adatok sikeresen feltöltve');
          // Sikeres feltöltés esetén bezárjuk a mapping dialog-ot is
          this.dialogRef.close({
            success: true,
            result: result.result
          });
        } else if (result && !result.success) {
          this.frontendLogService.addLog('❌ Feltöltési hiba: ' + (result.error || 'Ismeretlen hiba'));
          // Hiba esetén is bezárjuk a mapping dialog-ot és továbbítjuk a hibát
          this.dialogRef.close({
            success: false,
            error: result.error
          });
        } else {
          // Ha result false vagy üres, akkor a felhasználó csak bezárta a dialog-ot
          // Ebben az esetben nem zárjuk be a mapping dialog-ot
          this.frontendLogService.addLog('ℹ️ Data preview dialog bezárva feltöltés nélkül');
        }
      });
    }
  }

  private generateCombinedValue(combined: CombinedField, row: any): string {
    const values = combined.sourceColumns.map(column => {
      const columnIndex = this.data.columns.indexOf(column);
      let cellValue = '';
      
      if (Array.isArray(row) && row[columnIndex] !== undefined && row[columnIndex] !== null) {
        cellValue = String(row[columnIndex]);
      } else if (typeof row === 'object' && row[column] !== undefined && row[column] !== null) {
        cellValue = String(row[column]);
      }
      
      return cellValue.trim();
    }).filter(value => value.length > 0);

    if (values.length === 0) return '';

    // Template alkalmazása
    if (combined.template) {
      let result = combined.template;
      values.forEach((value, index) => {
        result = result.replace(`{${index}}`, value);
      });
      // Eltávolítjuk a fel nem használt placeholdereket
      result = result.replace(/\{[0-9]+\}/g, '').trim();
      return result;
    } else {
      return values.join(combined.separator || ', ');
    }
  }

  // Kombinált mezők kezelése
  addCombinedField(): void {
    this.combinedFields.push({
      targetDatabaseField: '',
      sourceColumns: [],
      separator: ', ',
      template: '{0}, {1} {2}'
    });
  }

  removeCombinedField(index: number): void {
    this.combinedFields.splice(index, 1);
  }

  addSourceColumn(combinedIndex: number, columnName: string): void {
    if (columnName && !this.combinedFields[combinedIndex].sourceColumns.includes(columnName)) {
      this.combinedFields[combinedIndex].sourceColumns.push(columnName);
    }
  }

  removeSourceColumn(combinedIndex: number, columnIndex: number): void {
    this.combinedFields[combinedIndex].sourceColumns.splice(columnIndex, 1);
  }

  getAvailableColumns(combinedIndex: number): string[] {
    const usedColumns = this.combinedFields[combinedIndex].sourceColumns;
    return this.data.columns.filter(column => !usedColumns.includes(column));
  }

  isCombinedFieldUsed(fieldValue: string, currentIndex: number): boolean {
    return this.combinedFields.some((combined, index) => 
      index !== currentIndex && combined.targetDatabaseField === fieldValue
    ) || this.columnMappings.some(mapping => mapping.databaseColumn === fieldValue);
  }

  getCombinedPreview(combined: CombinedField): string {
    if (combined.sourceColumns.length === 0) return '';
    
    // Minta adatok az első sorból
    const sampleData = combined.sourceColumns.map(column => {
      const columnIndex = this.data.columns.indexOf(column);
      if (this.data.data.length > 0) {
        const firstRow = this.data.data[0];
        if (Array.isArray(firstRow) && firstRow[columnIndex] !== undefined && firstRow[columnIndex] !== null) {
          return String(firstRow[columnIndex]);
        } else if (typeof firstRow === 'object' && firstRow[column] !== undefined && firstRow[column] !== null) {
          return String(firstRow[column]);
        }
      }
      return `[${column}]`;
    });

    // Template alkalmazása
    if (combined.template) {
      let result = combined.template;
      sampleData.forEach((data, index) => {
        result = result.replace(`{${index}}`, data || `[${combined.sourceColumns[index]}]`);
      });
      return result;
    } else {
      return sampleData.join(combined.separator || ', ');
    }
  }

  onCancel(): void {
    this.dialogRef.close({ proceedWithImport: false });
  }
}
